import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateApplicant,
  removeApplicant
} from 'products/nomad_insurance/state/application';
import useWindowSize from 'hooks/useWindowSize';
import AnimatedAccordion from 'products/nomad_insurance/components/AnimatedAccordion';
import { SCREEN_SIZES } from 'style';
import { PLANS } from 'products/nomad_insurance/constants';
import ApplicantDisplay from '../ApplicantDisplay';
import ApplicantForm from '../ApplicantForm';
import ApplicantDetails from '../ApplicantDetails';
import ChevronButton from '../ChevronButton';
import Buttons from './components/Buttons';
import { GroupMemberWrapper } from './styled';

const Applicant = ({ groupMember, memberPictureIndex = 0 }) => {
  const dispatch = useDispatch();
  const [isDeleting, setIsDeleting] = useState(false);

  // isFirstOpen represents the first "drawer" below the chevron button
  // on mobile it contains the edit and delete buttons
  // on desktop it contains the group member details
  const [isFirstOpen, setIsFirstOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const { offer, loadingGroupMemberID, planholder } = useSelector(
    (state) => state.nomadInsurance.application
  );
  const { addons, numberOfDays } = useSelector(
    (state) => state.nomadInsurance.purchaseInsurance.coverage
  );
  const hasUSCoverage = Boolean(
    addons.find((addon) => addon.value === PLANS.USA_COVERAGE.value)
  );
  const { width } = useWindowSize();
  const isMobile = width < SCREEN_SIZES.tabletPortrait;

  const groupMemberPrice = offer?.applicants.find(
    (x) => x.userId === groupMember?.userId
  )?.totalPrice;

  const handleEditGroupMember = (info) => {
    dispatch(updateApplicant(offer.applicationBundleId, groupMember.id, info));
    setIsFormOpen(false);
  };

  const handleDeleteGroupMember = (e, id) => {
    e.stopPropagation();
    setIsDeleting(true);
    setIsFirstOpen(false);
    setIsFormOpen(false);

    // Wait for framer animations to finish before deleting
    // Unmounting while frarmer animations are running causes errors
    setTimeout(() => {
      dispatch(removeApplicant(offer.applicationBundleId, id));
      setIsDeleting(false);
    }, 1000);
  };

  const handleChevronButtonClick = () => {
    if (isDeleting) return;
    if (isFirstOpen || isFormOpen) {
      setIsFirstOpen(false);
      setIsFormOpen(false);
      return;
    }
    setIsFirstOpen(true);
  };

  const handleEditClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDeleting) return;
    if (isFormOpen) {
      setIsFirstOpen(false);
      setIsFormOpen(false);
      return;
    }
    setIsFormOpen(true);
  };

  return (
    <GroupMemberWrapper>
      <ChevronButton
        isOpen={isFirstOpen || isFormOpen}
        onClick={handleChevronButtonClick}
      >
        <ApplicantDisplay
          groupMember={groupMember}
          homeCountry={planholder?.homeCountry}
          price={groupMemberPrice}
          loadingGroupMemberID={loadingGroupMemberID}
          memberPictureIndex={memberPictureIndex}
          contractDuration={numberOfDays}
        />
        {!isMobile && (
          <Buttons
            onDelete={(e) => handleDeleteGroupMember(e, groupMember.id)}
            onEdit={handleEditClick}
            disabled={isDeleting}
          />
        )}
      </ChevronButton>

      {isMobile && (
        <AnimatedAccordion isOpen={isFirstOpen}>
          <Buttons
            onDelete={(e) => handleDeleteGroupMember(e, groupMember.id)}
            onEdit={handleEditClick}
            disabled={isDeleting}
          />
        </AnimatedAccordion>
      )}

      {!isMobile && (
        <AnimatedAccordion isOpen={isFirstOpen && !isFormOpen}>
          <ApplicantDetails
            groupMember={groupMember}
            homeCountry={planholder?.homeCountry}
          />
        </AnimatedAccordion>
      )}

      <AnimatedAccordion isOpen={isFormOpen}>
        <ApplicantForm
          onSubmit={handleEditGroupMember}
          onCancel={() => setIsFormOpen(false)}
          groupMember={groupMember}
          hasUSCoverage={hasUSCoverage}
          planholder={planholder}
          hideFirstTopBorder
        />
      </AnimatedAccordion>
    </GroupMemberWrapper>
  );
};

Applicant.propTypes = {
  groupMember: PropTypes.object,
  memberPictureIndex: PropTypes.number
};

export default Applicant;
